import {
  ComputeBudgetProgram,
  Connection,
  Keypair,
  PublicKey,
  TransactionInstruction,
  TransactionMessage,
  clusterApiUrl,
  VersionedTransaction
} from '@solana/web3.js'
import {
  ASSOCIATED_TOKEN_PROGRAM_ID,
  getAssociatedTokenAddressSync,
  TOKEN_PROGRAM_ID,
  createAssociatedTokenAccountIdempotentInstruction
} from '@solana/spl-token'
import { WalletContextState } from '@solana/wallet-adapter-react'

import { ed25519 } from '@noble/curves/ed25519'

import axios from 'axios'
import bs58 from 'bs58'

const API_URL =
  process.env.REACT_APP_BACKEND_API_URL || 'https://api-stage.4.fun'

export const LABELS = {
  'change-wallet': 'Change wallet',
  connecting: 'Connecting ...',
  'copy-address': 'Copy address',
  copied: 'Copied',
  disconnect: 'Disconnect',
  'has-wallet': 'Connect',
  'no-wallet': 'connect wallet'
} as const

const isDev = true // @todo: isDev
const connectionURL = isDev
  ? 'https://testnode.4.fun/?api_key=f9308e8f21410f60843111c793f12e6a'
  : clusterApiUrl('mainnet-beta')
const connection = new Connection(connectionURL, 'confirmed')
const PROGRAM = new PublicKey('4funSZoXriGYKYwTAWTA6ip3FNDiEF4ir7Xxx5acNGr4')
const SYSTEM_PROGRAM = new PublicKey('11111111111111111111111111111111')
const FEE_ACCOUNT = new PublicKey('rQ7a19af4EP8uG8SAHUZMyFEMX9qAjq3R5cKEfdzqbq')
const REFERRAL_FEE_ACCOUNT = new PublicKey('12Lt9QNYThXDMr6niWBWjeomeS4ZqNPAh52iMgYr4fun')

export interface CreateTokenData {
  name: string
  symbol: string
  uri: string
}

export interface BuyTokenData {
  mint: PublicKey
  creator: PublicKey
  solToSpend: bigint
  minTokenToReceive: bigint
}

export interface SellTokenData {
  mint: PublicKey
  creator: PublicKey
  tokenToSpend: bigint
  minSolToReceive: bigint
}

export interface GetTokenData {
  mint: PublicKey
}

type Hex = Uint8Array | string

const sign = (message: Hex, secretKey: Hex) =>
  ed25519.sign(message, secretKey.slice(0, 32))

export const createToken = async (
  wallet: WalletContextState,
  tokenData: CreateTokenData,
  solToSpend: bigint = 1000000000n // TODO: after tests set to 0n.
) => {
  if (!wallet.publicKey || !wallet.signTransaction) {
    throw new Error('Wallet PublicKey missed')
  }

  const mintKey = Keypair.generate()
  const [ownerPubkey, curveBump] = PublicKey.findProgramAddressSync(
    [
      Buffer.from('token_owner', 'utf-8'),
      // enc.encode("token_owner"),
      mintKey.publicKey.toBuffer()
    ],
    PROGRAM
  )
  const ownerTokenAccount = getAssociatedTokenAddressSync(
    mintKey.publicKey,
    ownerPubkey,
    true
  )

  if (!mintKey.publicKey || !ownerPubkey) {
    throw 'Logical error! Unable to find nonce, which is a non-sence...'
  } else {
    console.log('Found MINT ADDRESS!!!', mintKey.publicKey.toBase58())
    console.log('Found CURVE ADDRESS!!!', ownerPubkey.toBase58(), curveBump)
  }

  const name = tokenData.name
  const symbol = tokenData.symbol
  const url = tokenData.uri
  const nameBuffer = Buffer.from(name, 'utf-8')
  const symbolBuffer = Buffer.from(symbol, 'utf-8')
  const urlBuffer = Buffer.from(url, 'utf-8')

  const data = Buffer.alloc(1000)
  let offset = 0

  // Selector = 1, create token
  data.writeUint8(1, offset)
  offset += 1

  // Name, Symbol or URI can not be longer than 255
  data.writeUint8(curveBump, offset)
  offset += 1
  data.writeUint8(urlBuffer.length, offset)
  offset += 1
  data.write(url, offset, 'utf-8')
  offset += urlBuffer.length
  data.writeUint8(nameBuffer.length, offset)
  offset += 1
  data.write(name, offset, 'utf-8')
  offset += nameBuffer.length
  data.writeUint8(symbolBuffer.length, offset)
  offset += 1
  data.write(symbol, offset, 'utf-8')
  offset += symbolBuffer.length

  console.log(data.slice(0, offset))

  const METAPLEX_PROGRAM = new PublicKey(
    'metaqbxxUerdq28cj1RbAWkYQm3ybzjb6a8bt518x1s'
  )
  const [metaplex_account] = PublicKey.findProgramAddressSync(
    [
      Buffer.from('metadata'),
      METAPLEX_PROGRAM.toBuffer(),
      mintKey.publicKey.toBuffer()
    ],
    METAPLEX_PROGRAM
  )
  console.log('Metaplex account', metaplex_account.toBase58())

  const createTokenIx = new TransactionInstruction({
    data: data.slice(0, offset),
    programId: PROGRAM,
    keys: [
      { pubkey: wallet.publicKey, isSigner: true, isWritable: true },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: SYSTEM_PROGRAM, isSigner: false, isWritable: false },
      {
        pubkey: ASSOCIATED_TOKEN_PROGRAM_ID,
        isSigner: false,
        isWritable: false
      },
      { pubkey: mintKey.publicKey, isSigner: true, isWritable: true },
      { pubkey: ownerPubkey, isSigner: false, isWritable: true },
      { pubkey: ownerTokenAccount, isSigner: false, isWritable: true },
      { pubkey: METAPLEX_PROGRAM, isSigner: false, isWritable: false },
      { pubkey: metaplex_account, isSigner: false, isWritable: true }
    ]
  })

  const instructions = [
    ComputeBudgetProgram.setComputeUnitLimit({ units: 250000 }),
    ComputeBudgetProgram.setComputeUnitPrice({ microLamports: 1000 }),
    createTokenIx
  ]

  if (solToSpend) {
    const buyTokenData: BuyTokenData = {
      mint: mintKey.publicKey,
      creator: wallet.publicKey,
      solToSpend: solToSpend,
      minTokenToReceive: 0n
    }
    const buyInstructions = await generateBuyInstruction(
      wallet.publicKey,
      buyTokenData
    )
    instructions.push(...buyInstructions)
  }

  await waitTransaction(connection, wallet, instructions, [mintKey])

  return mintKey.publicKey
}

export const getTokens = async (wallet: WalletContextState, mint: string) => {
  if (!wallet.publicKey) {
    throw new Error('Wallet PublicKey missed')
  }
  try {
    const key = new PublicKey(mint)
    const ownerTokenAccount = getAssociatedTokenAddressSync(
      key,
      wallet.publicKey,
      true
    )
    const tokenBalance = await connection.getTokenAccountBalance(
      ownerTokenAccount
    )
    return { tokenBalance }
  } catch (e) {
    return { tokenBalance: undefined }
  }
}

async function generateBuyInstruction(
  walletPub: PublicKey,
  orderData: BuyTokenData
) {
  const [ownerPubkey, curveBump] = PublicKey.findProgramAddressSync(
    [Buffer.from('token_owner', 'utf-8'), orderData.mint.toBuffer()],
    PROGRAM
  )
  const ownerTokenAccount = getAssociatedTokenAddressSync(
    orderData.mint,
    ownerPubkey,
    true
  )

  const data = Buffer.alloc(1000)
  let offset = 0

  const userTokenAccount = getAssociatedTokenAddressSync(
    orderData.mint,
    walletPub,
    false
  )

  const createTokenAccountIx =
    createAssociatedTokenAccountIdempotentInstruction(
      walletPub,
      userTokenAccount,
      walletPub,
      orderData.mint
    )

  // Selector = 2, buy token
  data.writeUint8(2, offset)
  offset += 1
  data.writeUint8(curveBump, offset)
  offset += 1
  data.writeBigUInt64LE(orderData.solToSpend, offset)
  offset += 8
  data.writeBigUInt64LE(orderData.minTokenToReceive, offset)
  offset += 8
  console.log('Creator: ' + orderData.creator.toBase58())
  const buyTokenIx = new TransactionInstruction({
    data: data.slice(0, offset),
    programId: PROGRAM,
    keys: [
      { pubkey: walletPub, isSigner: true, isWritable: true },
      { pubkey: orderData.creator, isSigner: false, isWritable: true },
      { pubkey: SYSTEM_PROGRAM, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ownerPubkey, isSigner: false, isWritable: true },
      { pubkey: orderData.mint, isSigner: false, isWritable: true },
      { pubkey: ownerTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: FEE_ACCOUNT, isSigner: false, isWritable: true },
      { pubkey: REFERRAL_FEE_ACCOUNT, isSigner: false, isWritable: true }
    ]
  })
  return [createTokenAccountIx, buyTokenIx]
}

async function generateSellInstruction(
  walletPub: PublicKey,
  orderData: SellTokenData
) {
  const [ownerPubkey, curveBump] = PublicKey.findProgramAddressSync(
    [Buffer.from('token_owner', 'utf-8'), orderData.mint.toBuffer()],
    PROGRAM
  )
  const ownerTokenAccount = getAssociatedTokenAddressSync(
    orderData.mint,
    ownerPubkey,
    true
  )

  const data = Buffer.alloc(1000)
  let offset = 0

  const userTokenAccount = getAssociatedTokenAddressSync(
    orderData.mint,
    walletPub,
    false
  )

  // Selector = 3, sell token
  data.writeUint8(3, offset)
  offset += 1
  data.writeUint8(curveBump, offset)
  offset += 1
  data.writeBigUInt64LE(orderData.tokenToSpend, offset)
  offset += 8
  data.writeBigUInt64LE(orderData.minSolToReceive, offset)
  offset += 8
  console.log('Creator: ' + orderData.creator.toBase58())
  const sellTokenIx = new TransactionInstruction({
    data: data.slice(0, offset),
    programId: PROGRAM,
    keys: [
      { pubkey: walletPub, isSigner: true, isWritable: true },
      { pubkey: orderData.creator, isSigner: false, isWritable: true },
      { pubkey: SYSTEM_PROGRAM, isSigner: false, isWritable: false },
      { pubkey: TOKEN_PROGRAM_ID, isSigner: false, isWritable: false },
      { pubkey: ownerPubkey, isSigner: false, isWritable: true },
      { pubkey: orderData.mint, isSigner: false, isWritable: true },
      { pubkey: ownerTokenAccount, isSigner: false, isWritable: true },
      { pubkey: userTokenAccount, isSigner: false, isWritable: true },
      { pubkey: FEE_ACCOUNT, isSigner: false, isWritable: true },
      { pubkey: REFERRAL_FEE_ACCOUNT, isSigner: false, isWritable: true }
    ]
  })
  return [sellTokenIx]
}

export const buyToken = async (
  wallet: WalletContextState,
  orderData: BuyTokenData,
  realToSpendCallback: (realToSpend: number) => void
) => {
  if (!wallet.publicKey) {
    throw new Error('Wallet PublicKey missed')
  }

  const buyInstructions = await generateBuyInstruction(
    wallet.publicKey,
    orderData
  )
  const realToSpend = await simulateTransaction(
    connection,
    wallet,
    buyInstructions
  )
  realToSpendCallback(realToSpend)
  return await waitTransaction(connection, wallet, buyInstructions)
}

export const sellToken = async (
  wallet: WalletContextState,
  orderData: SellTokenData,
  realToSpendCallback: (realToSpend: number) => void
) => {
  if (!wallet.publicKey) {
    throw new Error('Wallet PublicKey missed')
  }
  const sellInstructions = await generateSellInstruction(
    wallet.publicKey,
    orderData
  )
  const realToSpend = await simulateTransaction(
    connection,
    wallet,
    sellInstructions
  )
  realToSpendCallback(realToSpend)
  return await waitTransaction(connection, wallet, sellInstructions)
}

/**
 * Authorizes a wallet by requesting a nonce, signing it, and verifying with the backend.
 * @param wallet WalletContextState from Solana Wallet Adapter.
 * @returns {Promise<boolean>} True if authorization is successful, otherwise false.
 */
export async function authorize(
  wallet: WalletContextState
): Promise<boolean | string> {
  if (!wallet || !wallet.publicKey || !wallet.signMessage) {
    console.error('Wallet not connected or does not support signing.')
    return false
  }

  try {
    // 1️⃣ Request a nonce from the backend
    const publicKeyBase58 = wallet.publicKey.toBase58()
    const nonceResponse = await axios.get(
      `${API_URL}/backend/auth/nonce/${publicKeyBase58}`
    )
    const nonce = nonceResponse.data.nonce

    if (!nonce) {
      console.error('Failed to retrieve nonce from backend.')
      return false
    }

    // 2️⃣ Create the message to sign
    const message = `Authorize access to Solana wallet: ${publicKeyBase58} Nonce: ${nonce}`
    const encodedMessage = new TextEncoder().encode(message)

    // 3️⃣ Sign the message with the wallet
    const signatureUint8Array = await wallet.signMessage(encodedMessage)
    const signatureBase58 = bs58.encode(signatureUint8Array)

    console.log('Signed message:', signatureBase58)

    // 4️⃣ Send the signed message to the backend for verification
    const loginResponse = await axios.post(`${API_URL}/backend/auth/login`, {
      message: message,
      signature: signatureBase58,
      publicKey: publicKeyBase58,
      nonce: nonce
    })

    if (loginResponse.data.access_token) {
      console.log('✅ Authorization successful!')
      return loginResponse.data.access_token
    } else {
      console.error('❌ Authorization failed:', loginResponse.data.error)
      return false
    }
  } catch (error) {
    console.error('❌ Error during authorization:', error)
    return false
  }
}

async function waitTransaction(
  connection: Connection,
  wallet: WalletContextState,
  instructions: TransactionInstruction[],
  extraSigners: Keypair[] = []
) {
  const maxAttempts = 3
  let attemptCounter = 0
  let status = false

  if (!wallet.publicKey || !wallet.signTransaction) {
    throw new Error('Wallet PublicKey missed')
  }

  while (attemptCounter < maxAttempts && !status) {
    const latestBlockHash = await connection.getLatestBlockhash()
    const message = new TransactionMessage({
      payerKey: wallet.publicKey,
      recentBlockhash: latestBlockHash.blockhash,
      instructions: instructions
    }).compileToV0Message()

    const signedTransaction = await wallet.signTransaction(
      new VersionedTransaction(message)
    )
    signedTransaction.sign(extraSigners)
    const signature = await connection.sendTransaction(signedTransaction, {
      skipPreflight: true
    })
    console.log('tx sent, confirming...')
    try {
      const result = await connection.confirmTransaction({
        blockhash: latestBlockHash.blockhash,
        lastValidBlockHeight: latestBlockHash.lastValidBlockHeight,
        signature: signature
      })
      if (result.value.err) {
        console.log(result.value.err)
        attemptCounter = maxAttempts
      } else {
        status = true
      }
    } catch (error) {
      console.error(error)
      attemptCounter++
    }
    console.log('https://solscan.io/tx/' + signature + ' | Status: ' + status)
  }
  return status
}

async function simulateTransaction(
  connection: Connection,
  wallet: WalletContextState,
  instructions: TransactionInstruction[]
) {
  if (!wallet.publicKey || !wallet.signTransaction) {
    throw new Error('Wallet PublicKey missed')
  }

  const latestBlockHash = await connection.getLatestBlockhash()
  const message = new TransactionMessage({
    payerKey: wallet.publicKey,
    recentBlockhash: latestBlockHash.blockhash,
    instructions: instructions
  }).compileToV0Message()

  //const signedTransaction = await wallet.signTransaction(new VersionedTransaction(message));
  const balanceBefore = await connection.getBalance(wallet.publicKey)

  const simulationResults = await connection.simulateTransaction(
    new VersionedTransaction(message),
    {
      accounts: {
        encoding: 'base64',
        addresses: [wallet.publicKey.toBase58()]
      }
    }
  )
  if (simulationResults.value.err) {
    console.error('Simulation failed:', simulationResults.value.err)
    throw new Error('Simulation failed')
  }
  if (
    !simulationResults ||
    !simulationResults.value ||
    !simulationResults.value.accounts
  ) {
    throw new Error('Invalid simulationResults or account data.')
  }
  const balanceAfter = simulationResults.value.accounts[0]?.lamports ?? 0
  const spended = balanceBefore - balanceAfter
  console.log('Simulation: Value to spend in lamports ' + spended)
  return spended
}
